import {
    head,
    mapValues,
    map,
    orderBy,
    groupBy,
    get,
    isString,
    isObject,
    reduce,
    set
} from 'lodash';

export function toChartData(fundData, lineChartLabel) {
    if (typeof fundData !== 'object' || !Array.isArray(fundData.funds)) {
        return { FundsData: {} };
    }

    let fundsData = fundData.funds.map(fund => {
        return {
            name: fund.name || '',
            classification: fund.classification || '',
            data: fund.data || [],
            groupBy: fund.groupBy || '',
            seoName: fund.SeoName,
        };
    });

    if (lineChartLabel) {
        fundsData.push({
            name: lineChartLabel,
            data: fundData.data || [],
            type: "line",
            color: "#b3004d"
        });
    }

    return { FundsData: fundsData };
}

export function toTableData(fundData, locale) {
    if (typeof fundData !== 'object' || !Array.isArray(fundData.funds)) {
        return { FundsData: {} };
    }


    // Use the passed locale if it's a non-empty string, otherwise use the browser's locale
    const actualLocale = typeof locale === 'string' && locale.trim() !== '' ? locale : navigator.language;


    const fundDataValues = (fundData.funds || []).map(fund => {
        const value = fund.data && fund.data.length > 0 ? fund.data[0] : 0;
        let classificationType;
        switch (fund.classification) {
            case 'Return oriented':
                classificationType = 'risky';
                break;
            case 'Safety oriented':
                classificationType = 'defensive';
                break;
            default:
                console.warn(`LCClassification: invalid value: ${fund.classification}`);
                classificationType = 'other';
        }
        return {
            ...fund,
            Value: value,
            ClassificationType: classificationType,
            ValuePercentageFormatted: value.toLocaleString(actualLocale, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
        };
    }).sort((a, b) => {
        if (a.ClassificationType < b.ClassificationType) return 1;
        if (a.ClassificationType > b.ClassificationType) return -1;
        if (a.Value < b.Value) return -1;
        if (a.Value > b.Value) return 1;
        return 0;
    });

    const dropdownValues = [...new Set(fundDataValues.map(item => item.ClassificationType))].map((value, index) => {
        return {
            Value: value,
            Label: value,
            Index: index + 1
        };
    });

    const targetData = {
        FundDataValues: fundDataValues,
        DropdownValues: dropdownValues,
        AsOfDate: fundData.asOfDate
    };

    return targetData;
}

export function getGroupFunds(funds) {
    const defaultSorting = {
        accordion: {
            orderByKey: 'classification',
            orderByType: 'desc'
        },
        funds: {
            orderByKey: 'name',
            orderByType: 'asc'
        }
    };
    let sorting = {};
    Object.assign(sorting, defaultSorting);

    let groupedFunds = groupBy(funds, 'groupBy');
    groupedFunds = mapValues(groupedFunds, group =>
        orderBy(group, [sorting.funds.orderByKey], [sorting.funds.orderByType])
    );
    groupedFunds = map(groupedFunds, (value, key) => ({
        groupBy: key,
        funds: value
    }));
    groupedFunds = orderBy(
        groupedFunds,
        [
            o => {
                return get(head(o.funds), sorting.accordion.orderByKey);
            }
        ],
        [sorting.accordion.orderByType]
    );
    return groupedFunds;
}

export function formatValues(value, config) {
    if (isNaN(value)) {
        return '';
    }
    let formattedValue = value;
    const decimalLimit = Number(get(config, 'decimalLimit', 0));

    if (formattedValue && decimalLimit) {
        formattedValue = Number(formattedValue.toFixed(decimalLimit));
    }

    if (config.format) {
        try {
            const formatOptions = reduce(config.format, (result, formatConfig) => {
                if (isString(formatConfig)) {
                    result.push(formatConfig);
                } else if (isObject(formatConfig)) {
                    if (decimalLimit) {
                        set(formatConfig, 'minimumFractionDigits', decimalLimit);
                        set(formatConfig, 'maximumFractionDigits', decimalLimit);
                    }
                    result.push(formatConfig);
                }
                return result;
            }, []);

            if (formatOptions.length) {
                formattedValue = new Intl.NumberFormat(...formatOptions).format(formattedValue);
            }
        } catch (e) {
            console.error('Error formatting value:', e);
            formattedValue = value;
        }
    }

    if (config.prefixSymbol) {
        formattedValue = `${config.prefixSymbol}${formattedValue}`;
    }

    if (config.suffixSymbol) {
        formattedValue = `${formattedValue}${config.suffixSymbol}`;
    }

    return formattedValue;
}
